import { useEffect, useMemo, useRef, useState } from "react";
import * as D from "@radix-ui/react-dialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { formatCZK } from "@/lib/format";
import { roleLabel } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  X, ChevronLeft, ChevronRight, Users, Wallet, FlaskConical, Clock, CalendarPlus, ArrowUpDown, ShieldOff, ShieldCheck,
  Trash2, AlertTriangle, History, Settings2, Ban, CreditCard, Send, ExternalLink, FileText, Lock, GraduationCap, Check, Circle, Minus, ChevronDown, Compass,
} from "lucide-react";
import { toast } from "sonner";
import { SubscriptionCard, TestAccountCard, statusBanner } from "@/components/superadmin/Subscription";
import { actionsFor } from "@/components/superadmin/FirmActions";
import { PaymentHistoryCard } from "@/components/superadmin/PaymentHistory";
import { FirstInvoiceDialog } from "@/components/superadmin/FirstInvoice";
import { day, dayShort, daysTo, dny, plusDays, initials, colorOf, PAID_PLANS, CYCLE_MONTHS, CYCLE_ADV, CYCLE_PERIOD, isStripe, currentInvoice, activity } from "@/components/superadmin/firm";
import { ask } from "@/lib/confirm";

// Detail firmy (209) — jeden klik z tabule otevře VŠE: Přehled · Časová osa · Správa.
// Počítač: karta zprava (640 px) přes ztmavenou tabuli; telefon: list zespodu přes 92 % výšky.
// Šipkami ‹ › se přechází na další firmu ve stejném sloupci bez zavírání.

const TABS = [["prehled", "Přehled"], ["osa", "Časová osa"], ["sprava", "Správa"]];
const STATUS_LABEL = { trial: "Trial", active: "Platí", past_due: "Čeká na úhradu", canceled: "Zrušeno", suspended: "Pozastaveno" };
const TL_META = {
  payment_received: [CreditCard, "bg-success/15 text-success"],
  plati: [CreditCard, "bg-success/15 text-success"],
  admin_change_plan: [ArrowUpDown, "bg-brand/10 text-brand"],
  admin_override: [ArrowUpDown, "bg-brand/10 text-brand"],
  admin_suspend: [Ban, "bg-rose-500/15 text-rose-500"],
  admin_end_cooperation: [Ban, "bg-rose-500/15 text-rose-500"],
  payment_overdue: [Ban, "bg-rose-500/15 text-rose-500"],
  subscription_canceled: [Ban, "bg-rose-500/15 text-rose-500"],
  admin_reactivate: [ShieldCheck, "bg-success/15 text-success"],
  billing_invoice_issued: [FileText, "bg-brand/10 text-brand"],
  billing_invoice_due: [Clock, "bg-amber-500/15 text-amber-500"],
  billing_locked: [Lock, "bg-rose-500/15 text-rose-500"],
  admin_billing_settings: [Settings2, "bg-secondary text-muted-foreground"],
  admin_billing_postpone: [Clock, "bg-amber-500/15 text-amber-500"],
  admin_test_flag: [FlaskConical, "bg-violet-500/15 text-violet-500"],
  admin_offer_guide: [Compass, "bg-brand/10 text-brand"],
  registrace: [Clock, "bg-amber-500/15 text-amber-500"],
  admin_end_trial: [Clock, "bg-amber-500/15 text-amber-500"],
  admin_extend_trial: [Clock, "bg-amber-500/15 text-amber-500"],
  trial_mail: [Clock, "bg-amber-500/15 text-amber-500"],
  drip_mail: [Send, "bg-secondary text-muted-foreground"],
  drip_extend_trial: [Clock, "bg-amber-500/15 text-amber-500"],
  drip_unsubscribe: [Ban, "bg-secondary text-muted-foreground"],
};
const tlMeta = (e) => {
  if (e.kind === "admin_change_plan" && /odkaz/i.test(e.text || "")) return [Send, "bg-success/15 text-success"];
  return TL_META[e.kind] || [History, "bg-secondary text-muted-foreground"];
};
const TL_GROUP = {
  platby: ["payment_received", "payment_overdue", "subscription_canceled", "plati",
    "billing_invoice_issued", "billing_invoice_due", "billing_locked", "admin_billing_settings", "admin_billing_postpone"],
  tarif: ["admin_change_plan", "admin_override"],
  zamky: ["admin_suspend", "admin_reactivate", "admin_end_cooperation"],
  demo: ["registrace", "admin_end_trial", "admin_extend_trial", "trial_mail", "drip_mail", "drip_extend_trial", "drip_unsubscribe"],
};
const TL_FILTERS = [["vse", "Vše"], ["platby", "Platby"], ["tarif", "Tarif"], ["zamky", "Zámky"], ["demo", "Demo"]];
const dtt = (iso) => (iso ? `${day(iso)} ${new Date(iso).toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Prague" })}` : "—");
const deviceOf = (ua = "") => /iPhone|iPad/.test(ua) ? "iPhone" : /Android/.test(ua) ? "Android" : /Macintosh|Mac OS/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : "";
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
// Staré surové záznamy osy („Změna tarifu: {'plan': …}") zlidštit; nové už chodí česky.
const humanizeEvent = (text, plans) => {
  if (!text?.startsWith("Změna tarifu: {")) return text;
  const p = text.match(/'plan': '(\w+)'/)?.[1];
  const s = text.match(/'plan_status': '(\w+)'/)?.[1];
  if (!p && !s) return text;
  const parts = [];
  if (p) parts.push(`Tarif: ${plans?.[p]?.label || p}`);
  if (s) parts.push(`stav: ${STATUS_LABEL[s] || s}`);
  return parts.join(" · ");
};

const STATUS_TONE = {
  plati: "bg-success/15 text-success", demo_bezi: "bg-secondary text-muted-foreground",
  ceka_uhrada: "bg-brand/15 pp-ink", nezaplaceno: "bg-rose-500/15 text-rose-600 dark:text-rose-400",
  demo_vyprselo: "border border-rose-500/60 text-rose-600 dark:text-rose-400", pozastaveno: "border border-rose-500/60 text-rose-600 dark:text-rose-400",
  ukonceno: "bg-secondary text-muted-foreground", test: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
};
function statusTone(c) {
  if (c.is_test) return STATUS_TONE.test;
  if (c.display_status?.key === "plati" && c.billing?.phase === "invoice_due") return "bg-amber-500/15 text-amber-800 dark:text-amber-300";
  return STATUS_TONE[c.display_status?.key] || "bg-secondary";
}
function statusText(c) {
  const ds = c.display_status || {}, b = c.billing || {};
  if (ds.key === "plati" && b.phase === "invoice_due") return "Fakturovat";
  if (ds.key === "demo_bezi") return `Demo · do ${dayShort(c.trial_ends_at)}`;
  if (ds.key === "demo_vyprselo") return `Demo vypršelo ${dayShort(c.trial_ends_at)}`;
  if (b.phase === "first_due") return `První platba · splatnost ${dayShort(b.first_due)}`;     // 325
  if (ds.key === "ceka_uhrada" && ds.date) return `Čeká na úhradu · zámek ${dayShort(ds.date)}`;
  return ds.label || "—";
}

function Section({ title, icon: Icon, children, testid, tone }) {
  return (
    <section className={`border rounded-[18px] p-4 space-y-2.5 ${tone === "danger" ? "border-rose-500/50 bg-rose-500/5" : "border-border bg-card"}`} data-testid={testid}>
      <h3 className={`text-sm font-bold flex items-center gap-1.5 ${tone === "danger" ? "text-rose-600 dark:text-rose-400" : ""}`}>{Icon && <Icon size={16} className={tone === "danger" ? "" : "text-muted-foreground"} aria-hidden="true" />}{title}</h3>
      {children}
    </section>
  );
}
function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between gap-3 py-2 border-b border-border last:border-0" data-testid={testid}>
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-bold tabular-nums text-right">{value || "—"}</span>
    </div>
  );
}
function Tile({ label, value, pct, note, tone, title }) {
  return (
    <div className="bg-secondary rounded-[14px] p-3 min-w-0" title={title}>
      <div className="text-xs text-muted-foreground truncate">{label}</div>
      <div className={`text-lg font-black tabular-nums whitespace-nowrap truncate ${tone === "over" ? "text-rose-600 dark:text-rose-400" : ""}`}>{value}</div>
      {pct != null && <div className="h-1.5 rounded-full bg-background overflow-hidden mt-1.5"><div className={`h-full rounded-full ${tone === "over" ? "bg-rose-500" : tone === "full" ? "bg-amber-500" : "bg-success"}`} style={{ width: `${pct}%` }} /></div>}
      {note && <div className={`text-[11px] font-semibold mt-1 truncate ${tone === "warn" || tone === "full" ? "text-amber-700 dark:text-amber-300" : tone === "over" ? "text-rose-600 dark:text-rose-400" : "text-muted-foreground"}`}>{note}</div>}
    </div>
  );
}
function usageTile(label, { used, limit } = {}, unit) {
  if (limit === 0) return <Tile label={label} value="není v tarifu" note={used ? `použito ${used} (z dema)` : undefined} />;
  const unl = limit == null, over = !unl && used > limit, full = !unl && used === limit;
  return <Tile label={label} value={`${used ?? 0} / ${unl ? "∞" : limit}`} pct={unl ? null : Math.min(100, Math.round(((used || 0) / limit) * 100))}
    tone={over ? "over" : full ? "full" : undefined} note={over ? "přes limit" : full ? "plno" : unl ? "bez limitu" : `volno ${limit - (used || 0)}${unit ? ` ${unit}` : ""}`} />;
}

export default function CompanyPanel({ id, row, siblings, label, kpi, fa, isMobile, onClose, onNavigate, reload, run, busy, initialTab }) {
  const open = !!id;
  const [tab, setTabState] = useState(initialTab || "prehled");
  const [tlFilter, setTlFilter] = useState("vse");
  const scrollRef = useRef(null), headRef = useRef(null), contentRef = useRef(null), prevId = useRef(null), lastC = useRef(null);
  // Záložka se nastaví při otevření panelu; šipkami ‹ › se mezi firmami zůstává na stejné záložce.
  useEffect(() => {
    if (id && !prevId.current) { setTabState(initialTab || "prehled"); setTlFilter("vse"); }
    prevId.current = id;
    scrollRef.current?.scrollTo?.({ top: 0 });
  }, [id, initialTab]);
  // Přepnutí záložky: obsah od začátku, záložky zůstanou nahoře (hlavička odjede jen, když už odjela).
  const setTab = (k, anchor) => {
    setTabState(k);
    const el = scrollRef.current, lim = headRef.current?.offsetHeight ?? 0;
    if (el && el.scrollTop > lim) el.scrollTop = lim;
    if (anchor) requestAnimationFrame(() => contentRef.current?.querySelector(`[data-testid="${anchor}"]`)?.scrollIntoView({ block: "start", behavior: "smooth" }));
  };

  const detail = useQuery({ queryKey: ["sa", "company", id], queryFn: () => api.get(`/admin/companies/${id}`).then((r) => r.data), enabled: open });
  const timeline = useQuery({ queryKey: ["sa", "timeline", id], queryFn: () => api.get(`/admin/companies/${id}/timeline`).then((r) => r.data.events || []), enabled: open });
  const sel = detail.data && detail.data.company?.id === id ? detail.data : null;
  // Než doběhne detail, hlavička se kreslí z řádku tabule — panel je vidět hned.
  const live = sel ? { ...row, ...sel.company, has_subscription: !!sel.company.stripe_subscription_id, plan_label: sel.plan_label } : row;
  if (live) lastC.current = live;
  const c = live || (!open ? lastC.current : null);     // při zavírání se dokreslí poslední firma, ne „nenalezena“
  const notFound = open && !live && (detail.error?.response?.status === 404);

  const idx = siblings ? siblings.indexOf(id) : -1;
  const prev = idx > 0 ? siblings[idx - 1] : null, next = idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1] : null;
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      // Jen v samotném panelu (vnořená okna a rozbalovací seznamy jsou v portálu mimo něj), bez modifikátorů.
      if (e.defaultPrevented || e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
      if (!contentRef.current?.contains(e.target)) return;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target?.tagName) || e.target?.isContentEditable) return;
      if (["radio", "switch", "option", "combobox", "slider", "checkbox"].includes(e.target?.getAttribute?.("role"))) return;
      if (e.key === "ArrowLeft" && prev) onNavigate(prev);
      if (e.key === "ArrowRight" && next) onNavigate(next);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, prev, next, onNavigate]);

  const acts = c ? actionsFor(c) : [];
  const price = c ? (kpi?.plans?.[c.plan || "demo"]?.price ?? c.price ?? 0) : 0;

  return (
    <D.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none" />
        <D.Content ref={contentRef} aria-describedby={undefined} data-testid="sa-panel" tabIndex={-1}
          onOpenAutoFocus={(e) => { e.preventDefault(); e.currentTarget?.focus?.(); }}
          className={`fixed z-50 bg-background flex flex-col outline-none shadow-2xl motion-reduce:animate-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:duration-300 data-[state=closed]:duration-200 ${isMobile
            ? "inset-x-0 bottom-0 h-[92dvh] rounded-t-[24px] border-t border-border data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom"
            : "top-3 bottom-3 right-3 w-[640px] max-w-[calc(100vw-24px)] rounded-[24px] border border-border data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right"}`}>
          <div className="flex items-center gap-1.5 px-4 md:px-5 pt-2 md:pt-4 pb-2 shrink-0">
            {siblings && siblings.length > 1 && (
              <>
                <button type="button" onClick={() => prev && onNavigate(prev)} disabled={!prev} aria-label="Předchozí firma" data-testid="sa-panel-prev"
                  className="w-10 h-10 rounded-full bg-secondary grid place-items-center disabled:opacity-40"><ChevronLeft size={18} /></button>
                <button type="button" onClick={() => next && onNavigate(next)} disabled={!next} aria-label="Další firma" data-testid="sa-panel-next"
                  className="w-10 h-10 rounded-full bg-secondary grid place-items-center disabled:opacity-40"><ChevronRight size={18} /></button>
              </>
            )}
            <span className="text-sm font-semibold text-muted-foreground truncate ml-1">{siblings && idx >= 0 ? `${idx + 1} z ${siblings.length} · ${label}` : label}</span>
            <D.Close asChild>
              <button type="button" aria-label="Zavřít" className="ml-auto w-10 h-10 rounded-full bg-secondary grid place-items-center shrink-0" data-testid="sa-panel-close"><X size={18} /></button>
            </D.Close>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto overscroll-contain pb-[max(1.5rem,env(safe-area-inset-bottom))]">
            {!c ? (
              <div className="px-4 md:px-5 space-y-3">
                <D.Title className="sr-only">Detail firmy</D.Title>
                {notFound ? <p className="text-sm text-muted-foreground py-4">Firma nenalezena — možná byla smazána.</p>
                  : detail.isError ? <p className="text-sm text-rose-600 py-4">Detail se nepodařilo načíst. <button type="button" className="underline font-semibold" onClick={() => detail.refetch()}>Zkusit znovu</button></p>
                  : [0, 1, 2].map((i) => <div key={i} className={`${i ? "h-28" : "h-16"} rounded-[18px] bg-secondary animate-pulse motion-reduce:animate-none`} aria-hidden="true" />)}
              </div>
            ) : (
              <>
                <div ref={headRef} className="px-4 md:px-5 pb-3">
                  <div className="flex items-start gap-3">
                    <span aria-hidden="true" className="w-14 h-14 rounded-full grid place-items-center text-white text-lg font-bold shrink-0"
                      style={{ background: c.is_test ? "#6b6b74" : colorOf(c.id) }}>{initials(c.name)}</span>
                    <div className="min-w-0 flex-1">
                      <D.Title className="font-heading text-[24px] md:text-[26px] font-black tracking-tight leading-tight break-words" data-testid="detail-company-name">{c.name}</D.Title>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-sm">
                        <span className="text-muted-foreground">{c.ico ? `IČO ${c.ico}` : "IČO nevyplněno"}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap ${statusTone(c)}`} data-testid="detail-status-chip">{statusText(c)}</span>
                        <span className="font-bold whitespace-nowrap" data-testid="detail-plan-chip">{c.plan_label}{(c.plan || "demo") !== "demo" ? ` · ${formatCZK(price)}/měs` : ""}</span>
                      </div>
                    </div>
                  </div>
                  {acts.length > 0 && (
                    <div className="mt-4 flex flex-col sm:flex-row gap-2" data-testid="sa-panel-actions">
                      {acts.map((a, i) => (
                        <Button key={a.kind} variant={i ? "outline" : "default"} disabled={busy} onClick={() => run(a, c)}
                          className="h-11 rounded-full px-5 font-bold w-full sm:w-auto" data-testid={`sa-panel-act-${a.kind}`}>
                          <a.icon size={16} aria-hidden="true" /> {a.label}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="sticky top-0 z-10 bg-background px-4 md:px-5 pt-1 pb-3 border-b border-border">
                  <div role="tablist" aria-label="Detail firmy" className="grid grid-cols-3 gap-1 p-1 rounded-[14px] bg-secondary">
                    {TABS.map(([k, l]) => (
                      <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`detail-tab-${k}`}
                        className={`h-10 rounded-[11px] text-sm font-bold transition-colors ${tab === k ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
                    ))}
                  </div>
                </div>

                <div className="px-4 md:px-5 pt-4 space-y-3.5">
                  {!sel && detail.isError && <p className="text-sm text-rose-600">Detail se nepodařilo načíst. <button type="button" className="underline font-semibold" onClick={() => detail.refetch()}>Zkusit znovu</button></p>}
                  {!sel && !detail.isError && <div className="space-y-3" aria-hidden="true">{[0, 1, 2].map((i) => <div key={i} className="h-24 rounded-[18px] bg-secondary animate-pulse motion-reduce:animate-none" />)}</div>}
                  {sel && tab === "prehled" && <Overview sel={sel} c={c} price={price} onEdit={(anchor) => setTab("sprava", anchor)} />}
                  {tab === "osa" && <Timeline q={timeline} plans={kpi?.plans} filter={tlFilter} setFilter={setTlFilter} />}
                  {/* Správa zůstává připojená, ať se při přepnutí záložky neztratí rozepsaný tarif nebo vytvořený odkaz */}
                  {sel && <div hidden={tab !== "sprava"}><Admin key={c.id} sel={sel} c={c} price={price} kpi={kpi} fa={fa} reload={reload} onDeleted={onClose}
                    onShowPayments={() => { setTlFilter("platby"); setTab("osa"); }} /></div>}
                </div>
              </>
            )}
          </div>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

// ---------- Přehled ----------
function Overview({ sel, c, price, onEdit }) {
  const b = c.billing || {}, k = c.display_status?.key, months = CYCLE_MONTHS[b.cycle || "month"] || 1;
  const ni = currentInvoice(b);
  const active = sel.users.filter((u) => u.status === "active");
  const known = active.filter((u) => u.app_build);
  const current = sel.current_build ? known.filter((u) => u.app_build === sel.current_build).length : known.length;
  const outdated = known.length - current;
  const n = daysTo(c.trial_ends_at);
  const qc = useQueryClient();
  // 395: nabídnout průvodce jednomu člověku nebo všem, kdo ho neprošli — po příštím otevření aplikace uvidí přehled kroků
  const offer = async (u) => {
    const ids = u ? [u.id] : sel.users.filter(canOffer).map((x) => x.id);
    const msg = u ? `Nabídnout průvodce: ${u.name}?` : `Nabídnout průvodce všem, kdo ho neprošli (${ids.length})?`;
    if (!(await ask(`${msg}\n\nPo příštím otevření aplikace se sám otevře přehled kroků průvodce. Hotové kroky se u starých účtů počítají z celé historie.`, { ok: "Nabídnout" }))) return;
    try {
      const { data } = await api.post(`/admin/companies/${c.id}/offer-guide`, { user_ids: ids });
      toast.success(u ? `Průvodce nabídnut: ${u.name}` : `Průvodce nabídnut ${data.offered} lidem`);
      qc.invalidateQueries({ queryKey: ["sa", "company", c.id] });
      qc.invalidateQueries({ queryKey: ["sa", "timeline", c.id] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <>
      {c.is_test ? (
        <Section title="Testovací účet" icon={FlaskConical} testid="ov-test">
          <p className="text-sm text-muted-foreground">Nepočítá se do MRR ani statistik, nikdy se nezamkne, žádné faktury ani e-maily o platbách.</p>
        </Section>
      ) : k === "demo_bezi" || k === "demo_vyprselo" ? (
        <Section title="Demo" icon={Clock} testid="ov-demo">
          <p className="text-sm">{k === "demo_vyprselo"
            ? <>Demo vypršelo <b>{day(c.trial_ends_at)}</b> — jen čtení do <b>{day(plusDays(c.trial_ends_at, 14))}</b>, pak zámek.</>
            : <>Demo do <b>{day(c.trial_ends_at)}</b>{n != null ? ` · zbývá ${Math.max(0, n)} ${dny(Math.max(0, n))}` : ""}.</>}</p>
          <p className={`text-sm ${c.active_projects ? "text-muted-foreground" : "text-amber-700 dark:text-amber-300 font-semibold"}`}>
            {c.active_projects ? activity({ ...c, user_count: sel.usage.users.used, active_projects: sel.usage.active_projects.used }) : "Zatím nic nezaložili — zavolej a pomoz s nastavením."}
          </p>
          {c.checkout_sent_at && <p className="text-xs text-muted-foreground">Platební odkaz odeslán {dtt(c.checkout_sent_at)}.</p>}
        </Section>
      ) : k === "pozastaveno" ? (
        <Section title="Pozastaveno ručně" icon={Ban} testid="ov-paused">
          <p className="text-sm text-muted-foreground">Nouzová brzda — firma nemůže pracovat. „Obnovit firmu“ vrátí předchozí stav{c.status_at ? ` (pozastaveno ${day(c.status_at)})` : ""}.</p>
        </Section>
      ) : k === "ukonceno" ? (
        <Section title="Spolupráce ukončena" icon={Ban} testid="ov-ended">
          <p className="text-sm text-muted-foreground">{c.status_at ? `Ukončeno ${day(c.status_at)}. ` : ""}Majitel vidí kontakty pro návrat.</p>
        </Section>
      ) : isStripe(c) ? (
        <Section title="Předplatné · kartou" icon={CreditCard} testid="ov-stripe">
          <Row label="Platí od" value={c.paid_since ? day(c.paid_since) : null} />
          <Row label="Další platba" value={c.next_billing ? `${day(c.next_billing)} · ${formatCZK(price)}` : null} />
          <Row label="Karta" value={c.card_last4 ? `${cap(c.card_brand)} •••• ${c.card_last4}` : null} />
          <Row label="Poslední platba" value={c.last_payment_at ? `${day(c.last_payment_at)}${c.last_payment_amount != null ? ` · ${formatCZK(c.last_payment_amount)}` : ""}` : null} />
        </Section>
      ) : (
        <Section title="Předplatné · fakturou" icon={Wallet} testid="ov-subscription">
          {statusBanner(b, price * months)}
          <Row label="Zaplaceno do" value={b.phase === "first_due" ? `zatím nic · první faktura splatná ${day(b.first_due)}` : b.paid_until ? day(b.paid_until) : null} testid="ov-paid-until" />
          <Row label="Období" value={`${cap(CYCLE_ADV[b.cycle || "month"])} · ${formatCZK(price * months)} za ${CYCLE_PERIOD[b.cycle || "month"]}`} />
          <Row label="Spolupráce od" value={c.paid_since ? day(c.paid_since) : null} />
          {ni && <Row label="Faktura" value={<a href={ni.url || ni.public_url} target="_blank" rel="noopener noreferrer" className="pp-ink hover:underline inline-flex items-center gap-1">{ni.number} · {ni.status === "sent" ? "odeslaná" : ni.status === "paid" ? "zaplacená" : "neodeslaná"} <ExternalLink size={12} /></a>} />}
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <button type="button" onClick={() => onEdit("section-subscription")} className="text-sm font-semibold pp-ink hover:underline min-h-[32px]" data-testid="ov-edit-sub">Upravit předplatné ›</button>
            <button type="button" onClick={() => onEdit("section-history")} className="text-sm font-semibold pp-ink hover:underline min-h-[32px]" data-testid="ov-history">Doplnit historii plateb ›</button>
          </div>
        </Section>
      )}

      <Section title="Využití" icon={Users} testid="usage-meters">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {usageTile("Uživatelé", sel.usage.users)}
          {usageTile("Aktivní zakázky", sel.usage.active_projects)}
          {usageTile("OCR tento měsíc", sel.usage.ocr)}
          <Tile label="Aktuální verze" value={`${current} / ${known.length}`} tone={outdated ? "warn" : undefined}
            note={outdated ? `${outdated} na staré` : known.length ? "všichni aktuální" : "nikdo nehlásil"} title={sel.current_build ? `Aktuální build: ${sel.current_build}` : undefined} />
        </div>
      </Section>

      <Section title={`Uživatelé · ${sel.users.length}`} icon={Users} testid="detail-users">
        <GuideSummary users={sel.users} onOffer={() => offer(null)} />
        <div className="divide-y divide-border -my-1">
          {sel.users.map((u) => {
            const old = u.app_build && sel.current_build && u.app_build !== sel.current_build;
            return (
              <div key={u.id} className="py-2.5 text-sm" data-testid={`detail-user-${u.id}`}>
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-full bg-secondary grid place-items-center text-xs font-bold shrink-0" aria-hidden="true">{initials(u.name)}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{u.name}{u.status !== "active" && <span className="ml-1.5 text-xs font-normal text-muted-foreground">({u.status === "invited" ? "pozván" : "neaktivní"})</span>}</div>
                  <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                </div>
                <div className="text-right shrink-0 max-w-[55%]">
                  <div className="text-xs font-bold">{roleLabel(u.role)}</div>
                  <div className="text-[11px] text-muted-foreground">{u.last_login ? `naposledy ${day(u.last_login)}` : "nepřihlásil se"}</div>
                  <div className="text-[11px]" data-testid={`user-build-${u.id}`}>
                    {u.app_build
                      ? <span className={old ? "font-bold text-amber-700 dark:text-amber-300" : "text-muted-foreground"}>{old ? "stará verze" : "aktuální"} · {String(u.app_build).slice(0, 7)} · {day(u.app_build_at)}{deviceOf(u.app_ua) ? ` · ${deviceOf(u.app_ua)}` : ""}</span>
                      : <span className="text-muted-foreground">verze neznámá</span>}
                  </div>
                </div>
              </div>
              {/* 392: zaškolení pod celým řádkem — na telefonu by se vedle verze aplikace nevešlo */}
              <div className="pl-12"><UserGuide ob={u.onboarding} uid={u.id} onOffer={canOffer(u) ? () => offer(u) : null} /></div>
              </div>
            );
          })}
        </div>
      </Section>
    </>
  );
}

// ---------- Zaškolení (392) ----------
// Průvodce u každého člověka: certifikát, kolik kroků má hotových a na kterém stojí; klik rozbalí kroky.
// „Stojí na“ = první krok, který není hotový ani přeskočený. Pořadí kroků jako v průvodci na počítači.
const STALE_DAYS = 2;
// 395: komu jde průvodce nabídnout — aktivní bez certifikátu; ne kdo je v průvodci právě teď, uvítací okno („new“) ho čeká
// tak jako tak a nabídnutý ho ještě neviděl
const canOffer = (u) => u.status === "active" && u.onboarding && !u.onboarding.cert && u.onboarding.state !== "new" && !u.onboarding.offer_at
  && guideState(u.onboarding).tone !== "run";
function guideState(ob) {
  if (!ob) return null;
  if (ob.cert) return { tone: "ok", text: `Zaškolení hotové · certifikát ${ob.cert.no} · ${day(ob.cert.at)}` };
  if (!ob.started_at) {
    if (ob.offer_at) return { tone: "run", text: `Průvodce nabídnut ${day(ob.offer_at)} · čeká na otevření aplikace` };
    if (ob.offer_seen_at) return { tone: "warn", text: `Přehled průvodce otevřen ${day(ob.offer_seen_at)} · zaškolení nezačalo · z historie ${ob.done} / ${ob.total}` };
    if (ob.legacy) return { tone: "muted", text: `Starý účet, průvodce se nenabídl · z historie ${ob.done} / ${ob.total}` };
    return { tone: "muted", text: ob.state === "dismissed" ? "Zaškolení odložené (Teď ne)" : "Zaškolení nezačalo" };
  }
  const last = ob.updated_at || ob.started_at;
  const stale = (daysTo(last) ?? 0) <= -STALE_DAYS;
  const where = ob.stuck ? ` · ${ob.state === "dismissed" ? "odloženo u" : stale ? "stojí na" : "teď na"}: ${ob.stuck}` : " · zbývá převzít certifikát";
  return { tone: stale || ob.state === "dismissed" ? "warn" : "run", text: `Zaškolení ${ob.done} / ${ob.total}${where}`, last };
}
const GUIDE_TONE = { ok: "text-success", warn: "text-amber-700 dark:text-amber-300", run: "pp-ink", muted: "text-muted-foreground" };
function UserGuide({ ob, uid, onOffer }) {
  const [open, setOpen] = useState(false);
  const g = guideState(ob);
  if (!g) return <div className="text-[11.5px] text-muted-foreground mt-0.5" data-testid={`user-guide-${uid}`}>bez průvodce</div>;
  return (
    <div className="mt-0.5" data-testid={`user-guide-${uid}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className={`inline-flex items-start gap-1 text-left text-[11.5px] font-semibold hover:underline ${GUIDE_TONE[g.tone]}`}>
        <GraduationCap size={13} className="shrink-0 mt-[1px]" aria-hidden="true" />
        <span className="min-w-0">{g.text}{g.last && <span className="font-normal text-muted-foreground"> · naposledy {day(g.last)}</span>}</span>
        <ChevronDown size={13} className={`shrink-0 mt-[1px] transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {onOffer && <button type="button" onClick={onOffer} data-testid={`user-guide-offer-${uid}`}
        className="ml-2 text-[11.5px] font-bold text-brand hover:underline whitespace-nowrap">Nabídnout průvodce</button>}
      {open && (
        <ol className="mt-1.5 mb-1 space-y-1 rounded-[12px] bg-secondary/60 p-2.5" data-testid={`user-guide-steps-${uid}`}>
          {ob.steps.map((st) => {
            const here = !ob.cert && ob.stuck === st.label;
            const Icon = st.done ? Check : st.skipped ? Minus : Circle;
            return (
              <li key={st.key} className={`flex items-center gap-2 text-[12px] ${here ? "font-bold text-amber-700 dark:text-amber-300" : st.done ? "" : "text-muted-foreground"}`}>
                <Icon size={13} className={`shrink-0 ${st.done ? "text-success" : ""}`} aria-hidden="true" />
                <span className={`min-w-0 ${st.skipped && !st.done ? "line-through" : ""}`}>{st.label}</span>
                {st.skipped && !st.done && <span className="text-[11px] no-underline">přeskočeno</span>}
                {here && <span className="ml-auto text-[11px] whitespace-nowrap">stojí tady</span>}
              </li>
            );
          })}
          {ob.started_at && <li className="pt-1 text-[11px] text-muted-foreground">začátek {day(ob.started_at)}{ob.finished_at ? ` · dokončeno ${day(ob.finished_at)}` : ""}</li>}
        </ol>
      )}
    </div>
  );
}
function GuideSummary({ users, onOffer }) {
  const list = users.filter((u) => u.onboarding && u.status !== "inactive");
  if (!list.length) return null;
  const done = list.filter((u) => u.onboarding.cert).length;
  const run = list.filter((u) => !u.onboarding.cert && u.onboarding.started_at).length;
  const none = list.length - done - run;
  const can = users.filter(canOffer).length;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="guide-summary">
        <GraduationCap size={14} aria-hidden="true" />
        Zaškolení: <b className="text-success">{done} hotové</b> · <b className="text-foreground">{run} rozpracované</b> · {none} nezačalo
      </p>
      {can > 1 && <button type="button" onClick={onOffer} data-testid="guide-offer-all"
        className="text-xs font-bold text-brand hover:underline">Nabídnout průvodce všem ({can})</button>}
    </div>
  );
}

// ---------- Časová osa ----------
function Timeline({ q, plans, filter: f, setFilter: setF }) {
  const [all, setAll] = useState(false);
  const events = useMemo(() => q.data || [], [q.data]);
  const list = useMemo(() => (f === "vse" ? events : events.filter((e) => (TL_GROUP[f] || []).includes(e.kind))), [events, f]);
  if (q.isPending) return <div className="h-40 rounded-[18px] bg-secondary animate-pulse motion-reduce:animate-none" aria-hidden="true" />;
  if (q.isError) return <p className="text-sm text-rose-600">Časovou osu se nepodařilo načíst. <button type="button" className="underline font-semibold" onClick={() => q.refetch()}>Zkusit znovu</button></p>;
  return (
    <div data-testid="company-timeline">
      <div className="flex flex-wrap gap-1 p-1 rounded-[14px] bg-secondary mb-3" role="tablist" aria-label="Druh událostí" data-testid="timeline-filters">
        {TL_FILTERS.map(([k, l]) => {
          const cnt = k === "vse" ? events.length : events.filter((e) => TL_GROUP[k].includes(e.kind)).length;
          return (
            <button key={k} type="button" role="tab" aria-selected={f === k} onClick={() => { setF(k); setAll(false); }} data-testid={`tl-filter-${k}`}
              className={`h-9 px-3 rounded-[11px] text-sm font-bold whitespace-nowrap transition-colors ${f === k ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {l} <span className="text-xs tabular-nums opacity-70">{cnt}</span>
            </button>
          );
        })}
      </div>
      {(all ? list : list.slice(0, 20)).map((e, i) => {
        const [Icon, cls] = tlMeta(e);
        return (
          <div key={i} className="flex items-start gap-3 py-2.5 border-b border-border last:border-0" data-testid={`timeline-event-${i}`}>
            <div className={`w-[34px] h-[34px] rounded-full grid place-items-center shrink-0 ${cls}`}><Icon size={16} aria-hidden="true" /></div>
            <div className="min-w-0 flex-1 pt-0.5">
              <div className={`text-sm font-semibold ${["trial_mail", "drip_mail"].includes(e.kind) ? "text-muted-foreground font-normal" : ""}`}>{humanizeEvent(e.text, plans)}</div>
              {e.by && <div className="text-xs text-muted-foreground">{e.by}</div>}
            </div>
            <div className="text-right shrink-0">
              {e.amount != null && <div className="text-sm font-extrabold tabular-nums" data-testid={`tl-amount-${i}`}>{formatCZK(e.amount)}</div>}
              <div className="text-xs text-muted-foreground tabular-nums">{day(e.at)}</div>
              {e.invoice_url && <a href={e.invoice_url} target="_blank" rel="noopener noreferrer" data-testid={`tl-invoice-${i}`} className="text-xs font-semibold pp-ink">Faktura ↗</a>}
            </div>
          </div>
        );
      })}
      {list.length === 0 && <div className="text-sm text-muted-foreground py-2">Žádné události.</div>}
      {!all && list.length > 20 && (
        <button type="button" onClick={() => setAll(true)} className="text-sm font-semibold pp-ink hover:underline mt-2" data-testid="timeline-more">Zobrazit starší ({list.length - 20})</button>
      )}
    </div>
  );
}

// ---------- Správa ----------
function Admin({ sel, c, price, kpi, fa, reload, onDeleted, onShowPayments }) {
  const [busy, setBusy] = useState(false);
  const init = useMemo(() => ({ plan: (c.plan || "demo") === "demo" ? "zaklad" : c.plan, plan_status: c.plan_status === "past_due" ? "past_due" : "active" }), [c.plan, c.plan_status]);
  const [planForm, setPlanForm] = useState(init);
  const [extra, setExtra] = useState(String(c.plan_override?.extra_users || ""));
  const [payLink, setPayLink] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [firstOpen, setFirstOpen] = useState(false);
  useEffect(() => { setPlanForm(init); }, [init]);
  const isDemo = (c.plan || "demo") === "demo";
  const isSuspended = c.plan_status === "suspended";
  const planDirty = planForm.plan !== init.plan || planForm.plan_status !== init.plan_status;
  const extraDirty = String(Number(extra) || 0) !== String(c.plan_override?.extra_users || 0);

  const act = async (path, body, okMsg) => {
    setBusy(true);
    try { const { data } = await api.post(path, body || {}); toast.success(okMsg); await reload(); return data || {}; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return null; }
    finally { setBusy(false); }
  };
  const makePayLink = async () => {
    const d = await act(`/admin/companies/${c.id}/billing/checkout`, { plan: planForm.plan, origin_url: window.location.origin }, "Platební odkaz vytvořen");
    if (!d?.checkout_url) return;
    setPayLink(d.checkout_url);
    try { await navigator.clipboard.writeText(d.checkout_url); } catch { /* schránka nemusí být dostupná */ }
    toast.success(d.emailed ? "Odkaz odeslán majiteli e-mailem a zkopírován." : "E-mail se nepodařilo odeslat — pošli odkaz ručně.");
  };
  const sendPayLink = async () => {
    const d = await act(`/admin/companies/${c.id}/billing/send-link`, { origin_url: window.location.origin }, "Odkaz na platbu vytvořen");
    if (!d) return;
    setPayLink(d.url);
    const email = sel.users.find((u) => u.role === "OWNER")?.email;
    toast.success(d.emailed ? `Odkaz odeslán na ${email || "e-mail majitele"}` : "E-mail se nepodařilo odeslat — pošli odkaz ručně.");
  };
  const delCompany = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/companies/${c.id}`, { data: { confirm: confirmName } });
      const n = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Firma smazána (${n} záznamů, ${data.storage_objects_deleted} souborů).`);
      setDelOpen(false);
      onDeleted();
      await reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const planOptions = PAID_PLANS.map(([k, l]) => <SelectItem key={k} value={k}>{kpi?.plans?.[k] ? `${l} · ${kpi.plans[k].price.toLocaleString("cs-CZ")} Kč/měs` : l}</SelectItem>);

  return (
    <>
      {isDemo ? (
        <>
          <Section title="Demo" icon={Clock} testid="section-demo">
            <p className="text-sm" data-testid="demo-state-line">
              {c.display_status?.key === "demo_vyprselo"
                ? <>Demo vypršelo <b>{day(c.trial_ends_at)}</b> — jen pro čtení do <b>{day(plusDays(c.trial_ends_at, 14))}</b>, pak zámek.</>
                : c.display_status?.key === "demo_bezi" ? <>Demo běží do <b>{day(c.trial_ends_at)}</b>.</> : <>Demo je {c.display_status?.label?.toLowerCase()}.</>}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button variant="outline" disabled={busy} onClick={() => act(`/admin/companies/${c.id}/extend-trial`, {}, "Demo prodlouženo o 14 dní")} data-testid="action-extend-trial"><CalendarPlus size={15} /> Prodloužit +14</Button>
              <Button variant="outline" disabled={busy} onClick={() => act(`/admin/companies/${c.id}/end-trial`, {}, "Demo ukončeno")} data-testid="action-end-trial"><Clock size={15} /> Ukončit demo</Button>
            </div>
          </Section>
          <Section title="Aktivace tarifu" icon={Wallet} testid="section-activate">
            {/* 325: domluvený klient s vystavenou první fakturou — automatika pak hlídá splatnost */}
            <Button disabled={busy} onClick={() => setFirstOpen(true)} className="w-full md:w-auto font-bold" data-testid="action-first-invoice">Převést na klienta · první faktura</Button>
            <p className="text-xs text-muted-foreground">Do splatnosti faktury plný přístup, den po splatnosti upozornění majiteli, za 3 dny zámek. Platbu z Fakturoidu spáruje sám.</p>
            <FirstInvoiceDialog open={firstOpen} onOpenChange={setFirstOpen} c={c} plans={kpi?.plans} faConfigured={!!fa?.configured}
              onDone={async (ok, err) => { if (err) { toast.error(err); return; } toast.success(ok); await reload(); }} />
            <div className="text-xs font-semibold text-muted-foreground pt-1">Nebo bez faktury</div>
            <Select value={planForm.plan} onValueChange={(v) => setPlanForm({ ...planForm, plan: v })}>
              <SelectTrigger className="h-10 bg-card" data-testid="plan-select" aria-label="Tarif"><SelectValue /></SelectTrigger>
              <SelectContent>{planOptions}</SelectContent>
            </Select>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button variant="outline" disabled={busy} onClick={() => act(`/admin/companies/${c.id}/plan`, { plan: planForm.plan, plan_status: "active" }, "Tarif aktivován")} data-testid="action-activate-plan"><ArrowUpDown size={14} /> Aktivovat převodem</Button>
              <Button variant="outline" disabled={busy} onClick={makePayLink} data-testid="action-pay-link">{c.checkout_sent_at ? "Poslat odkaz znovu" : "Poslat platební odkaz"}</Button>
            </div>
            <p className="text-xs text-muted-foreground" data-testid="checkout-sent-line">{c.checkout_sent_at ? `Naposledy odesláno ${dtt(c.checkout_sent_at)}` : "Odkaz zatím neodeslán."}</p>
            {payLink && <Input readOnly value={payLink} onFocus={(e) => e.target.select()} className="h-9 text-xs" data-testid="pay-link-url" />}
          </Section>
        </>
      ) : (
        <>
          {!c.is_test && <SubscriptionCard c={c} price={price} plans={kpi?.plans} faConfigured={!!fa?.configured} onChanged={reload} />}
          {!c.is_test && !isStripe(c) && <PaymentHistoryCard c={c} price={price} faConfigured={!!fa?.configured} onChanged={reload} />}
          <Section title="Tarif" icon={Wallet} testid="section-plan">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-center">
              <Select value={planForm.plan} onValueChange={(v) => setPlanForm({ ...planForm, plan: v })}>
                <SelectTrigger className="h-10 bg-card" data-testid="plan-select" aria-label="Tarif"><SelectValue /></SelectTrigger>
                <SelectContent>{planOptions}</SelectContent>
              </Select>
              <div className="flex gap-1 p-1 rounded-full bg-secondary w-fit" data-testid="pay-status-toggle">
                {[["active", "Platí"], ["past_due", "Čeká na úhradu"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setPlanForm({ ...planForm, plan_status: k })} data-testid={`pay-status-${k}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${planForm.plan_status === k ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
                ))}
              </div>
            </div>
            <Button size="sm" disabled={busy || !planDirty} variant={planDirty ? "default" : "secondary"} className="w-full sm:w-auto" onClick={() => act(`/admin/companies/${c.id}/plan`, planForm, "Tarif uložen")} data-testid="action-change-plan"><ArrowUpDown size={14} /> Uložit tarif</Button>
          </Section>
          <Section title="Platby" icon={CreditCard} testid="section-payments">
            <div>
              <Row label="Poslední platba" testid="pay-last" value={c.last_payment_at ? `${day(c.last_payment_at)}${c.last_payment_amount != null ? ` · ${formatCZK(c.last_payment_amount)}` : ""}` : null} />
              <Row label="Další platba" testid="pay-next" value={(c.billing?.mode === "invoice" ? c.billing.next_start : c.next_billing) ? `${day(c.billing?.mode === "invoice" ? c.billing.next_start : c.next_billing)} · ${formatCZK(price)}` : null} />
              <Row label="Karta" testid="pay-card" value={c.card_last4 ? `${cap(c.card_brand)} •••• ${c.card_last4}` : "nepřidána · platí převodem"} />
              <Row label="Odkaz odeslán" testid="pay-link-sent" value={(c.payment_link_sent_at || c.checkout_sent_at) ? dtt([c.payment_link_sent_at, c.checkout_sent_at].filter(Boolean).sort().pop()) : null} />
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button size="sm" variant="outline" disabled={busy} onClick={c.stripe_customer_id ? sendPayLink : makePayLink} data-testid="action-send-pay-link">
                <Send size={14} /> {(c.payment_link_sent_at || c.checkout_sent_at) ? "Poslat odkaz znovu" : "Poslat odkaz na platbu"}
              </Button>
              <Button size="sm" variant="ghost" onClick={onShowPayments} data-testid="pay-show-all">Všechny platby v časové ose ›</Button>
            </div>
            {payLink && <Input readOnly value={payLink} onFocus={(e) => e.target.select()} className="h-9 text-xs" data-testid="pay-link-url" />}
          </Section>
          {c.stripe_subscription_id && (
            <Section title="Předplatné ve Stripe" icon={Wallet} testid="section-stripe">
              <Row label="Stav" value={c.plan_status === "canceled" ? "Žádné" : "Aktivní"} testid="stripe-state" />
              <Row label="Cyklus" value={c.plan_cycle === "year" ? "Ročně" : "Měsíčně"} testid="stripe-cycle" />
              <a href={`https://dashboard.stripe.com/subscriptions/${c.stripe_subscription_id}`} target="_blank" rel="noopener noreferrer" data-testid="stripe-open-link"
                className="h-10 w-full rounded-xl border border-border bg-card font-bold text-sm inline-flex items-center justify-center gap-1.5 hover:bg-secondary">Otevřít ve Stripe <ExternalLink size={14} /></a>
            </Section>
          )}
        </>
      )}

      <TestAccountCard c={c} onChanged={reload} />

      <Section title="Limity" icon={Users} testid="section-limits">
        <Label className="text-xs" htmlFor="sa-extra">Uživatelé navíc nad tarif</Label>
        <div className="flex gap-2">
          <Input id="sa-extra" type="number" inputMode="numeric" min="0" value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="0" className="h-10 bg-card w-full min-w-0" data-testid="override-input" />
          <Button size="sm" className="h-10" disabled={busy || !extraDirty} onClick={() => act(`/admin/companies/${c.id}/override`, { extra_users: Number(extra) || 0 }, "Limit uložen")} data-testid="action-override">Uložit</Button>
        </div>
      </Section>

      <Section title="Zastavit firmu" icon={AlertTriangle} tone="danger" testid="danger-zone">
        <div className="space-y-2" data-testid="section-suspend">
          <p className="text-xs text-muted-foreground">Pozastavit = nouzová brzda, okamžitý zámek; Obnovit vrátí předchozí stav.</p>
          {isSuspended ? (
            <Button variant="outline" disabled={busy} className="w-full text-success" onClick={() => act(`/admin/companies/${c.id}/reactivate`, {}, "Firma obnovena")} data-testid="action-reactivate"><ShieldCheck size={15} /> Obnovit firmu</Button>
          ) : (
            <Button variant="outline" disabled={busy} className="w-full text-rose-600" onClick={() => act(`/admin/companies/${c.id}/suspend`, {}, "Firma pozastavena")} data-testid="action-suspend"><ShieldOff size={15} /> Pozastavit firmu</Button>
          )}
          {!isDemo && c.plan_status !== "canceled" && (
            <Button variant="outline" disabled={busy} className="w-full text-rose-600"
              onClick={async () => { if (await ask("Ukončit spolupráci? Firma se zamkne, majitel uvidí kontakty pro návrat.")) act(`/admin/companies/${c.id}/end-cooperation`, {}, "Spolupráce ukončena"); }}
              data-testid="action-end-cooperation"><Ban size={14} /> Ukončit spolupráci</Button>
          )}
        </div>
        <div className="pt-2 border-t border-rose-500/30 space-y-2">
          <p className="text-xs text-muted-foreground">Smazání nevratně odstraní firmu a VŠECHNA její data (zakázky, doklady, docházku, deník, sklad, soubory i uživatele).</p>
          <Button variant="outline" disabled={busy} className="w-full text-rose-600 border-rose-500/50 hover:bg-rose-500/10"
            onClick={() => { setConfirmName(""); setDelOpen(true); }} data-testid="action-delete-company"><Trash2 size={14} /> Smazat firmu</Button>
        </div>
      </Section>

      <Dialog open={delOpen} onOpenChange={(o) => { setDelOpen(o); if (!o) setConfirmName(""); }}>
        <DialogContent data-testid="delete-company-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-600">Smazat firmu natrvalo</DialogTitle>
            <DialogDescription>Tato akce je nevratná — smaže všechna data firmy.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">Smaže firmu <b>{c.name}</b> včetně <b>{sel.users.length}</b> uživatelů a všech souvisejících dat. Pro potvrzení opiš přesný název firmy:</p>
            <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={c.name} className="h-11" data-testid="delete-confirm-input" autoFocus />
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-1">
              <Button variant="outline" onClick={() => setDelOpen(false)} data-testid="delete-cancel">Zrušit</Button>
              <Button disabled={busy || confirmName !== c.name} onClick={delCompany} className="bg-rose-600 hover:bg-rose-700 text-white font-bold" data-testid="delete-confirm-btn"><Trash2 size={16} /> Smazat natrvalo</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
